import React, { useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Form, Formik } from "formik";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";
import {
  ModalPageHeader,
  ModalPageBody,
  ModalPageFooter,
  ModalPageWrapper,
  ModalPageHeading,
} from "../../../organisms/ModalPage";
import RadioButtonGroup from "../../../molecules/RadioButtonGroup/RadioButtonGroup";
import { update_signup_form_values } from "../../../../store/actions/signupFlowData";
import { MentalHealthValidationSchema } from "../../../../data/Validation/EligibilityCheckerValidation";
import FormikCheckbox from "../../../atoms/FormikCheckbox";
import CheckboxGroupContainer from "../../../atoms/CheckboxGroupContainer";
import { mentalHealthConditions } from "../../../../enums/profile/MentalHealthCondition";
import { MentalHealthProps } from "./MentalHealthRoot";
import { pageTitleSuffix } from "../../../../data/eligibilityChecker";
import { lowercaseFirstLetter } from "../../../../utils/StringUtils";

const MentalHealth: React.FC<MentalHealthProps> = (props: MentalHealthProps) => {
  const { programmeTitle, mentalHealthPageData, nextStepUrl, healthConditions } = props;

  const dispatch = useDispatch();
  const signupFormValues = useSelector((state: any) => state.signupFlowData.signupFormValues);
  let history = useHistory();
  let { path } = useRouteMatch();

  let mentalHealth = null;
  if (!!healthConditions?.mentalConditions) {
    mentalHealth = healthConditions?.mentalConditions.length > 0 ? "yes" : "no";
  }
  const initialValues = {
    mentalHealth: signupFormValues?.mentalHealth ?? mentalHealth ?? "",
    mentalConditions:
      signupFormValues?.mentalConditions ??
      healthConditions?.mentalConditions.map(x => lowercaseFirstLetter(x.toString())) ??
      [],
  };

  const yesNoOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const page_title = `Mental health | ${programmeTitle} | ${pageTitleSuffix}`;

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: page_title,
    });
  }, [page_title]);

  const handleSubmit = values => {
    if (values.mentalHealth === "no") {
      history.push(nextStepUrl);
      // Save values to store
      dispatch(update_signup_form_values({ ...values, mentalConditions: [] }));
    } else {
      if (values.mentalConditions.includes("eatingDisorder")) {
        //Trigger google analytics event on ineligible eating disorder
        window.gtag("event", "ineligibleEatingDisorder", {
          event_category: "Eligibility Checker",
          event_label: "Ineligible Eating Disorder",
        });
        history.push(`${path}/ineligible-eating-disorder/fail`);
      } else {
        history.push(`${path}/mental-health-management`);
      }
      // Save values to store
      dispatch(update_signup_form_values(values));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={MentalHealthValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ isSubmitting, values, touched, errors }) => (
        <Form className="eligibility-checker-form mental-health-form">
          <Helmet>
            <title>{page_title}</title>
          </Helmet>
          <ModalPageWrapper>
            <ModalPageBody className="with-sticky-footer">
              <ModalPageHeader
                backButtonOnClick={() => {
                  history.goBack();
                }}
              >
                Checking we're a good fit
              </ModalPageHeader>

              <ModalPageHeading>{mentalHealthPageData?.questions[0].question}</ModalPageHeading>
              <RadioButtonGroup
                options={yesNoOptions}
                name="mentalHealth"
                hasErrors={errors["mentalHealth"] && touched["mentalHealth"]}
              />
              <Accordion square={true} elevation={0} className="eligibility-checker__accrodion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="large" />}
                  aria-controls={`mental-question-explanation`}
                >
                  <span>{mentalHealthPageData?.questions[0].whyQuestion}</span>
                </AccordionSummary>
                <AccordionDetails>
                  <span>{mentalHealthPageData?.questions[0].whyQuestionDetails}</span>
                </AccordionDetails>
              </Accordion>

              {values.mentalHealth === "yes" && (
                <>
                  <ModalPageHeading>Select any that apply to you</ModalPageHeading>
                  <CheckboxGroupContainer name="mentalConditions">
                    {mentalHealthConditions.map(item => (
                      <FormikCheckbox
                        key={item.value}
                        id={item.value}
                        name="mentalConditions"
                        label={item.label}
                        value={item.value}
                      />
                    ))}
                  </CheckboxGroupContainer>
                </>
              )}
            </ModalPageBody>
            <ModalPageFooter className="sticky-footer">
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                Continue
              </Button>
            </ModalPageFooter>
          </ModalPageWrapper>
        </Form>
      )}
    </Formik>
  );
};

export default MentalHealth;
